<template>
  <component
    :is="componentType"
    v-model="contentModel"
    :settings="settings"
    :is-builder="isBuilder"
    :is-last="isLast"
    :is-flush="isFlush"
    :block-index="blockIndex"
    :content-index="contentIndex"
    :width="width"
    :drawer-container="drawerContainer"
  />
</template>

<script>
import { defineComponent, computed } from 'vue'
import config from '../../utils/config'
import Image from './Image.vue'
import Text from './Text.vue'
import Title from './Title.vue'
import Button from './Button.vue'
import Divider from './Divider.vue'
import Html from './Html.vue'
import Signature from './Signature.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-email-template-content></ak-email-template-content>
   */
  name: 'AkEmailTemplateContent',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Image.name]: Image,
    [Text.name]: Text,
    [Title.name]: Title,
    [Button.name]: Button,
    [Divider.name]: Divider,
    [Html.name]: Html,
    [Signature.name]: Signature,
  },

  /**
   * @type {Boolean}
   * @description This prevents all element attributes that are not
   * defined in the props object from being applied to the rendered
   * component root element, the exception to this is "class" all
   * of the given classes would be merged with the component
   * defined ones.
   */
  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @description The content to render/edit
     */
    modelValue: {
      type: Object,
      default: () => ({
        content:
          '<p>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod ' +
          'tempor incididunt ut labore et dolore magna aliqua.</p>',
        type: 'text',
        style: {},
      }),
    },

    /**
     * @type {Object}
     * @description The settings for the template
     */
    settings: {
      type: Object,
      default: () => ({ ...config.defaultTemplateStyles }),
    },

    /**
     * @type {Boolean}
     * @description Determines if we are to render the block for the
     * builder component (drag and drop editor)
     */
    isBuilder: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Determines if the content item is the last in its column
     */
    isLast: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Determines if block has flush spacing
     */
    isFlush: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Number}
     * @description Block index
     */
    blockIndex: {
      type: Number,
      default: 0,
    },

    /**
     * @type {Number}
     * @description Content index
     */
    contentIndex: {
      type: Number,
      default: 0,
    },

    /**
     * @type {Number}
     * @description Width of the image
     */
    width: {
      type: Number,
      default: null,
    },

    /**
     * @type {String}
     * @description The container in which to position the drawers
     */
    drawerContainer: {
      type: String,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const contentModel = computed({
      get() {
        return props.modelValue
      },
      set(val) {
        emit('update:modelValue', val)
      },
    })

    /**
     * @returns {String}
     * @description Returns the type of component that we need to
     * render for the content
     */
    const componentType = computed(() => {
      return `ak-email-template-${contentModel.value.type}`
    })

    return {
      componentType,
      contentModel,
    }
  },
})
</script>
